import React from 'react';
import {
  useCreateRemoveRetailPriceRangeFilters,
  removeDspClicks
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useMetricByWeekId } from 'src/serverProxy/useMetricByWeekId';
import { useMetricByWeekIdV2 } from 'src/components/BeaconV2Components/SplineChartV2';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TotalTrafficDonutChart from './TotalTrafficDonutChart';
import { TrafficBarCharts } from './TrafficBarCharts';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { onDisplayAdvertisingMode } from 'src/store/modules/user/selectors';
import { useAppSelector } from 'src/utils/Hooks';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

/**
 * Shows donut chart and bar charts for the top section of Total Traffic
 */
export const TotalTrafficTopSection = () => {
  const mainEntityType = useMainEntityType();
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  // TODO: Use agg-traffic index when available for topics
  const isAdvertisingDisplayMode = useAppSelector(onDisplayAdvertisingMode);
  const index = mainEntityType === 'searchtermlist' || !isAdvertisingDisplayMode ? 'traffic' : 'aggregated-traffic';

  const trafficRequestBuilder = React.useCallback(
    (builder: AdvancedSearchRequestBuilder) =>
      removeDspClicks(removeRetailPriceRangeFilters()(builder), !isAdvertisingDisplayMode),
    [removeRetailPriceRangeFilters, isAdvertisingDisplayMode]
  );

  const {
    primaryTotal: totalClicks,
    comparisonTotal: totalClicksComparison,
    isLoading: totalLoading
  } = useMetricByWeekIdV2({
    indexName: index,
    fieldName: 'totalClicks',
    requestBuilder: trafficRequestBuilder
  });
  const {
    primaryTotal: organicClicks,
    comparisonTotal: organicClicksComparison,
    isLoading: organicLoading
  } = useMetricByWeekIdV2({
    indexName: index,
    fieldName: 'organicClicks',
    requestBuilder: trafficRequestBuilder
  });
  const {
    primaryTotal: paidTraffic,
    comparisonTotal: paidTrafficComparison,
    isLoading: paidLoading
  } = useMetricByWeekIdV2({
    indexName: index,
    fieldName: 'adClicks',
    requestBuilder: trafficRequestBuilder
  });
  const {
    primaryTotal: otherTraffic,
    comparisonTotal: otherTrafficComparison,
    isLoading: otherLoading
  } = useMetricByWeekIdV2({
    indexName: index,
    fieldName: 'otherClicks',
    requestBuilder: trafficRequestBuilder
  });
  const { primaryTotal: dspClicks, isLoading: dspLoading } = useMetricByWeekId({
    indexName: 'advertisingDisplay',
    fieldName: 'dpv',
    requestBuilder: (builder: AdvancedSearchRequestBuilder) => {
      return builder.apply(removeRetailPriceRangeFilters());
    }
  });

  const loading = organicLoading || paidLoading || otherLoading || dspLoading || totalLoading;

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <ChartContextProvider
        group={CHART_CONTEXT_GROUPS.BEACON_TOTAL_TRAFFIC_TOP.name}
        chartName={CHART_NAMES.BEACON_TOTAL_TRAFFIC_DONUT}
      >
        <TotalTrafficDonutChart
          loading={loading}
          dspClicks={dspClicks}
          organicClicks={organicClicks}
          otherTraffic={otherTraffic}
          paidTraffic={paidTraffic}
          totalClicks={totalClicks}
        />
      </ChartContextProvider>
      {shouldShowBeaconProTopics() ? (
        <TrafficBarCharts
          loading={loading}
          organicTraffic={{
            comparison: organicClicksComparison,
            current: organicClicks
          }}
          otherTraffic={{
            comparison: otherTrafficComparison,
            current: otherTraffic
          }}
          paidTraffic={{
            comparison: paidTrafficComparison,
            current: paidTraffic
          }}
          totalTraffic={totalClicks}
          totalTrafficComparison={totalClicksComparison}
        />
      ) : (
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.BEACON_TOTAL_TRAFFIC_SPLINE.name}
          chartName={CHART_NAMES.BEACON_TOTAL_TRAFFIC_SPLINE_CHART}
        >
          <div
            style={{
              width: '624px'
            }}
          >
            <BeaconComparableSplineChart
              indexName={mainEntityType === 'searchtermlist' ? 'traffic' : 'aggregated-traffic'} // TODO: Use agg-traffic index when available for topics
              fieldName="totalClicks"
              showTitleSummary={false}
              splineChartLoadingProps={{
                width: 543
              }}
              chartOptionsOverride={{
                chart: {
                  height: 300
                }
              }}
              requestBuilder={(builder) =>
                removeDspClicks(removeRetailPriceRangeFilters()(builder), !isAdvertisingDisplayMode)
              }
            />
          </div>
        </ChartContextProvider>
      )}
    </Flex>
  );
};
